/**
 * Centralized Route Path Enums
 * Single source of truth for all application routes
 */

/**
 * Public routes - accessible without authentication
 */
export enum PublicRoutes {
  HOME = '/',
  LOGIN = '/login',
  SIGNED_IN = '/signedin',
  ONBOARDING = '/onboarding',
  TERMS = '/terms',
  PRIVACY = '/privacy',
  ABOUT = '/about',
  KNOWLEDGE_HUB = '/knowledge-hub',
  BLOG_POSTS = '/blog-posts'
}

/**
 * Gamer-specific routes
 * All routes under /gamer/* path
 */
export enum GamerRoutes {
  BASE = '/gamer',
  DASHBOARD = '/gamer/dashboard-gamer',
  MY_PEOPLE = '/gamer/my-people',
  LATEST_SESSION_FEEDBACK = '/gamer/latest-session-feedback',
  UPCOMING_ACTIVITIES = '/gamer/upcoming-activities',
  ALL_UPCOMING_ACTIVITIES = '/gamer/upcoming-activities/all',
  ONBOARDING = '/gamer/onboarding',
  MY_PROFILE = '/gamer/my-profile',
  ACCOUNT_SETTINGS = '/gamer/account-settings',
  PERMISSIONS_INSTRUCTIONS = '/gamer/permissions-instructions',
  FAQ = '/gamer/faq',
  TUTORIAL_VIDEOS = '/gamer/tutorial-videos',
  ACTIVITY_DETAILS = '/gamer/activity-details',
  DEEPER_TEAM_ANALYSIS = '/gamer/feedback/deeper-analysis/team',
  DEEPER_INDIVIDUAL_ANALYSIS = '/gamer/feedback/deeper-analysis/individual',
  ACHIEVEMENTS = '/gamer/achievements',
  UNSEEN_ACHIEVEMENTS = '/gamer/unseen-achievements',
  AI_COACH = '/gamer/ai-coach',
  SPRINT_CALL = '/gamer/sprint-call',
  ARIA_COACH = '/gamer/coach',
  // Dev tools routes
  DEV_TOOLS = '/gamer/dev-tools',
  DEV_TOOLS_VOICE_LOTTIE = '/gamer/dev-tools/voice-lottie',
  DEV_TOOLS_BUBBLE_LOTTIE = '/gamer/dev-tools/bubble-lottie',
  DEV_TOOLS_VOICE_ANIMATION_AUDIO = '/gamer/dev-tools/voice-animation-audio',
  DEV_TOOLS_RECHARTS_GLASS = '/gamer/dev-tools/recharts-glass',
  DEV_TOOLS_COMMUNICATION_FEELING = '/gamer/dev-tools/communication-feeling',
  DEV_TOOLS_STACKED_CHARTS = '/gamer/dev-tools/stacked-charts',
  DEV_TOOLS_COMPARISON_BAR = '/gamer/dev-tools/comparison-bar',
  DEV_TOOLS_TEAM_HEALTH_HEART = '/gamer/dev-tools/team-health-heart',
  DEV_TOOLS_GLINT_REVEAL = '/gamer/dev-tools/glint-reveal',
  DEV_TOOLS_GENERATE_REPORT_VISUALS = '/gamer/dev-tools/generate-report-visuals',
  DEV_TOOLS_FUNNEL_GRAPH = '/gamer/dev-tools/funnel-graph',
  DEV_TOOLS_FUNNEL_GRAPH_V2 = '/gamer/dev-tools/funnel-graph-v2',
  DEV_TOOLS_FUNNEL_GRAPH_V3 = '/gamer/dev-tools/funnel-graph-v3',
  DEV_TOOLS_TEAM_INFORMATION_SHARES = '/gamer/dev-tools/team-information-shares',
  DEV_TOOLS_ACHIEVEMENTS = '/gamer/dev-tools/achievements',
  DEV_TOOLS_PERSONALITY_TRAITS = '/gamer/dev-tools/personality-traits',
  DEV_TOOLS_BO_CLEANUP = '/gamer/dev-tools/bo-cleanup'
}

/** What the sprint call page reads from its address: the sprint and the activity whose call it holds. */
export enum SprintCallQueryParam {
  SPRINT_ID = 'sprintId',
  ACTIVITY_ID = 'activityId'
}

/**
 * Facilitator-specific routes
 * All routes under /facilitator/* path
 */
export enum FacilitatorRoutes {
  BASE = '/facilitator',
  DASHBOARD = '/facilitator/dashboard-facilitator',
  TRAINING_SPRINT_MANAGEMENT = '/facilitator/training-sprint-management',
  SPRINT_CREATION = '/facilitator/sprint-creation',
  SPRINT_DETAILS = '/facilitator/sprint/:sprintId',
  SESSIONS = '/facilitator/sessions',
  UPCOMING_ACTIVITIES = '/facilitator/upcoming-activities',
  MANAGE_REPORTS = '/facilitator/manage-reports',
  GENERATE_REPORT = '/facilitator/generate-report',
  MANAGE_REFLECTIONS = '/facilitator/manage-reflections',
  FACILITATE_REFLECTION = '/facilitator/facilitate-reflection/:reflectionId',
  FACILITATE_REFLECTION_NEW = '/facilitator/facilitate-reflection/new',
  QA = '/facilitator/qa',
  PROFILE = '/facilitator/profile',
  PERMISSIONS_INSTRUCTIONS = '/facilitator/permissions-instructions',
  USERS = '/facilitator/back-office',
  AI_COACH = '/facilitator/ai-coach/:sessionId',
  // Dev tools routes
  DEV_TOOLS = '/facilitator/dev-tools',
  DEV_TOOLS_VOICE_LOTTIE = '/facilitator/dev-tools/voice-lottie',
  DEV_TOOLS_BUBBLE_LOTTIE = '/facilitator/dev-tools/bubble-lottie',
  DEV_TOOLS_VOICE_ANIMATION_AUDIO = '/facilitator/dev-tools/voice-animation-audio',
  DEV_TOOLS_RECHARTS_GLASS = '/facilitator/dev-tools/recharts-glass',
  DEV_TOOLS_COMMUNICATION_FEELING = '/facilitator/dev-tools/communication-feeling',
  DEV_TOOLS_STACKED_CHARTS = '/facilitator/dev-tools/stacked-charts',
  DEV_TOOLS_COMPARISON_BAR = '/facilitator/dev-tools/comparison-bar',
  DEV_TOOLS_TEAM_HEALTH_HEART = '/facilitator/dev-tools/team-health-heart',
  DEV_TOOLS_GLINT_REVEAL = '/facilitator/dev-tools/glint-reveal',
  DEV_TOOLS_GENERATE_REPORT_VISUALS = '/facilitator/dev-tools/generate-report-visuals',
  DEV_TOOLS_FUNNEL_GRAPH = '/facilitator/dev-tools/funnel-graph',
  DEV_TOOLS_FUNNEL_GRAPH_V2 = '/facilitator/dev-tools/funnel-graph-v2',
  DEV_TOOLS_FUNNEL_GRAPH_V3 = '/facilitator/dev-tools/funnel-graph-v3',
  DEV_TOOLS_TEAM_INFORMATION_SHARES = '/facilitator/dev-tools/team-information-shares',
  DEV_TOOLS_ACHIEVEMENTS = '/facilitator/dev-tools/achievements',
  DEV_TOOLS_PERSONALITY_TRAITS = '/facilitator/dev-tools/personality-traits',
  DEV_TOOLS_BO_CLEANUP = '/facilitator/dev-tools/bo-cleanup',
  ORGANIZATION_MEMBERS = '/facilitator/organization/members',
  ORGANIZATION_INVITES = '/facilitator/organization/invites',
  ORGANIZATION_SETTINGS = '/facilitator/organization/settings'
}

/**
 * Shared routes - accessible by both gamer and facilitator
 * These routes work under both /gamer/* and /facilitator/* paths
 */
export enum SharedRoutes {
  // Game routes
  GAME_CENTER = '/game-center',
  PLAY_GAME_WITH_INTRODUCTION = '/play-game-with-introduction',
  PLAY_GAME_WITHOUT_INTRODUCTION = '/play-game-without-introduction'
}

/**
 * Helper to get full path for shared routes under a specific role
 */
/** Router state that asks the coach page to open in a given focus; read once and then cleared. */
export const COACH_FOCUS_ROUTE_STATE_KEY = 'coachFocus'

export const getSharedRoutePath = (route: SharedRoutes, role: 'gamer' | 'facilitator'): string => {
  const base = role === 'gamer' ? GamerRoutes.BASE : FacilitatorRoutes.BASE
  return `${base}${route}`
}

/**
 * Helper to extract route from full path
 */
export const extractSharedRoute = (fullPath: string): SharedRoutes | null => {
  const sharedRouteValues = Object.values(SharedRoutes)
  for (const route of sharedRouteValues) {
    if (fullPath.includes(route)) {
      return route as SharedRoutes
    }
  }
  return null
}

/**
 * Helper to detect role from path
 */
export const getRoleFromPath = (path: string): 'gamer' | 'facilitator' | null => {
  const normalized = path
  if (normalized.startsWith(GamerRoutes.BASE)) return 'gamer'
  if (normalized.startsWith(FacilitatorRoutes.BASE)) return 'facilitator'
  return null
}
